import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, SettingsIcon, GlobeIcon, SmartphoneIcon, UserXIcon, KeyIcon, ScanFaceIcon, UserIcon, PhoneIcon, ChevronRightIcon, CheckCircleIcon, AlertCircleIcon, Trash2Icon, ShieldIcon, MailIcon, MapPinIcon, LockIcon, ClockIcon, EyeIcon, EyeOffIcon, XIcon, DeleteIcon } from 'lucide-react';
import { useTheme } from '../utils/theme';
import { useTranslation } from '../utils/translations';
type ProfileStep = 'home' | 'account_settings' | 'language' | 'devices' | 'deactivate' | 'login_method' | 'face_id' | 'username' | 'contact' | 'change_password';
interface ProfileScreenProps {
  onBack: () => void;
}
const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? '100%' : '-100%',
    opacity: 0
  }),
  center: {
    x: 0,
    opacity: 1
  },
  exit: (direction: number) => ({
    x: direction < 0 ? '100%' : '-100%',
    opacity: 0
  })
};
// Mock Data
const LINKED_ACCOUNTS = [{
  id: '1',
  title: 'Current Account',
  number: 'PK02BOPM92302274632656',
  isDefault: true,
  digitalConsent: true
}, {
  id: '2',
  title: 'Savings Account',
  number: 'PK07BOPM92302274638821',
  isDefault: false,
  digitalConsent: false
}];
const REGISTERED_DEVICES = [{
  id: 'd1',
  name: 'iPhone 14 Pro',
  os: 'iOS 16.5',
  lastActive: 'Just now',
  isCurrent: true
}, {
  id: 'd2',
  name: 'Samsung Galaxy S23',
  os: 'Android 13',
  lastActive: '2 days ago',
  isCurrent: false
}];
const TAKEN_USERNAMES = ['kashish', 'kashish123', 'kshewani', 'kashish.s'];
export function ProfileScreen({
  onBack
}: ProfileScreenProps) {
  const {
    isDark,
    isPlatinum
  } = useTheme();
  const {
    t,
    language,
    setLanguage,
    isRtl
  } = useTranslation();
  const [step, setStep] = useState<ProfileStep>('home');
  const [direction, setDirection] = useState(1);
  const [showToast, setShowToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const navigateTo = (newStep: ProfileStep) => {
    setDirection(1);
    setStep(newStep);
  };
  const goBack = () => {
    if (step === 'home') {
      onBack();
    } else {
      setDirection(-1);
      setStep('home');
    }
  };
  const showNotification = (message: string, type: 'success' | 'error' = 'success') => {
    setShowToast({
      message,
      type
    });
    setTimeout(() => setShowToast(null), 3000);
  };
  const isLight = !isDark && !isPlatinum;
  const bgClass = isLight ? 'bg-[#F5F3F1]' : isPlatinum ? 'bg-gradient-to-b from-[#1C1C1E] to-[#2A2A2C]' : 'bg-gradient-to-b from-[#1A0E08] to-[#2D1810]';
  const cardBg = isLight ? 'bg-white' : isPlatinum ? 'bg-[#3A3A3C]/90' : 'bg-[#3D2518]/90';
  const textColor = isLight ? 'text-[#1A1A1A]' : 'text-white';
  const subTextColor = isLight ? 'text-[#6B6B6B]' : isPlatinum ? 'text-[#A0A0A5]' : 'text-[#C4B5AA]';
  const borderColor = isLight ? 'border-[#E5E5E5]' : isPlatinum ? 'border-[#4A4A4C]' : 'border-[#4A2D1D]';
  // --- Sub-screen States ---
  // Account Settings
  const [accounts, setAccounts] = useState(LINKED_ACCOUNTS);
  // Devices
  const [devices, setDevices] = useState(REGISTERED_DEVICES);
  const [deviceToDelete, setDeviceToDelete] = useState<string | null>(null);
  const [deviceOtp, setDeviceOtp] = useState(['', '', '', '', '', '']);
  // Deactivate
  const [deactivateType, setDeactivateType] = useState<'temp' | 'perm'>('temp');
  const [deactivateDays, setDeactivateDays] = useState(1);
  // Login Method
  const [loginMethod, setLoginMethod] = useState<'biometric' | 'mpin' | 'password'>('biometric');
  // Face ID
  const [faceIdEnabled, setFaceIdEnabled] = useState(true);
  // Username
  const [newUsername, setNewUsername] = useState('');
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
  // Contact
  const [contactMobile, setContactMobile] = useState('+92 302 2746326');
  const [contactEmail, setContactEmail] = useState('kashish@example.com');
  const [contactAddress, setContactAddress] = useState('Clifton, Karachi');
  // Change Password
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  // Inline MPIN confirmation sheet
  const [mpin, setMpin] = useState('');
  const [mpinError, setMpinError] = useState(false);
  const mpinActionRef = useRef<(() => void) | null>(null);
  const [showMpinSheet, setShowMpinSheet] = useState(false);
  const requestMpin = (onConfirm: () => void) => {
    setMpin('');
    setMpinError(false);
    mpinActionRef.current = onConfirm;
    setShowMpinSheet(true);
  };
  const handleMpinDigit = (d: string) => {
    if (mpin.length >= 4) return;
    const next = mpin + d;
    setMpin(next);
    setMpinError(false);
    if (next.length === 4) {
      // Accept any 4-digit MPIN in this prototype
      setTimeout(() => {
        setShowMpinSheet(false);
        const action = mpinActionRef.current;
        mpinActionRef.current = null;
        setMpin('');
        action?.();
      }, 250);
    }
  };
  // --- Helpers ---
  const handleUsernameCheck = (val: string) => {
    setNewUsername(val);
    if (val.length < 4) {
      setUsernameAvailable(null);
      return;
    }
    setUsernameAvailable(!TAKEN_USERNAMES.includes(val.toLowerCase()));
  };
  const renderHeader = (title: string) => <div className="px-6 pt-12 pb-4 flex items-center justify-between relative z-10" data-id="element-2027" data-mp="iad3so4y7ri4">
      <button onClick={goBack} className={`w-10 h-10 rounded-full flex items-center justify-center ${isLight ? 'bg-white shadow-sm' : 'bg-white/10'}`} data-id="element-2028" data-mp="dcl4h91z533w">
        <ChevronLeftIcon className={`w-6 h-6 ${isLight ? 'text-[#661806]' : 'text-white'}`} data-id="element-2029" data-mp="27ojdpb0a9yzs" />
      </button>
      <h1 className={`text-lg font-semibold ${textColor}`} data-id="element-2030" data-mp="5zc7qvgeq787">{title}</h1>
      <div className="w-10" data-id="element-2031" data-mp="aqlrjff9ygc6" /> {/* Spacer */}
    </div>;
  // --- Screens ---
  const renderHome = () => {
    const menuItems = [{
      id: 'account_settings',
      icon: SettingsIcon,
      label: 'Account Settings',
      color: '#3B82F6'
    }, {
      id: 'language',
      icon: GlobeIcon,
      label: 'Language Selection',
      color: '#10B981'
    }, {
      id: 'devices',
      icon: SmartphoneIcon,
      label: 'Device Management',
      color: '#8B5CF6'
    }, {
      id: 'deactivate',
      icon: UserXIcon,
      label: 'Deactivate Account',
      color: '#EF4444'
    }, {
      id: 'login_method',
      icon: KeyIcon,
      label: 'Default Login Method',
      color: '#F59E0B'
    }, {
      id: 'face_id',
      icon: ScanFaceIcon,
      label: 'Facial Recognition',
      color: '#EC4899'
    }, {
      id: 'username',
      icon: UserIcon,
      label: 'Update Username',
      color: '#6366F1'
    }, {
      id: 'change_password',
      icon: LockIcon,
      label: 'Change Password',
      color: '#0EA5E9'
    }, {
      id: 'contact',
      icon: PhoneIcon,
      label: 'Update Contact Details',
      color: '#14B8A6'
    }];
    return <div className="px-6 pb-24 space-y-4" data-id="element-2032" data-mp="1vk15re85lgwb">
        {/* Profile Card */}
        <div className={`p-6 rounded-2xl ${cardBg} shadow-sm border ${borderColor} flex items-center gap-4`} data-id="element-2033" data-mp="1rk0qxsh573oq">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#661806] to-[#D95A1A] flex items-center justify-center text-white text-2xl font-bold shadow-md" data-id="element-2034" data-mp="13u6ehc0qjh50">
            K
          </div>
          <div data-id="element-2035" data-mp="1cmi2kr8dmsht">
            <h2 className={`text-xl font-bold ${textColor}`} data-id="element-2036" data-mp="1cmzxnkjaz0o5">
              Kashish Shewani
            </h2>
            <p className={`text-sm ${subTextColor}`} data-id="element-2037" data-mp="17svpixyawmlh">@kashish</p>
            <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#10B981]/10 text-[#10B981] text-xs font-medium" data-id="element-2038" data-mp="k430llg3jf0d">
              <CheckCircleIcon className="w-3 h-3" data-id="element-2039" data-mp="g1zkukme9o9a" /> Verified
            </div>
          </div>
        </div>

        {/* Menu Grid */}
        <div className="grid grid-cols-1 gap-3" data-id="element-2040" data-mp="19wwev1tm8jds">
          {menuItems.map(item => {
          const Icon = item.icon;
          return <button key={item.id} onClick={() => navigateTo(item.id as ProfileStep)} className={`w-full p-4 rounded-2xl ${cardBg} border ${borderColor} flex items-center gap-4 transition-transform active:scale-[0.98]`} data-id="element-2041" data-mp="1dyi1hevycc16">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{
              backgroundColor: `${item.color}15`
            }} data-id="element-2042" data-mp="1nvcx9q0ubybr">
                  <Icon className="w-5 h-5" style={{
                color: item.color
              }} data-id="element-2043" data-mp="1xobm99091qvw" />
                </div>
                <span className={`flex-1 text-left font-medium ${textColor}`} data-id="element-2044" data-mp="21o2i3r4mux7a">
                  {item.label}
                </span>
                <ChevronRightIcon className={`w-5 h-5 ${subTextColor}`} data-id="element-2045" data-mp="2bn1lovajk2nf" />
              </button>;
        })}
        </div>
      </div>;
  };
  const renderAccountSettings = () => <div className="px-6 pb-24 space-y-6" data-id="element-2046" data-mp="2ls9ussu5b5ec">
      <div className="space-y-4" data-id="element-2047" data-mp="2grcdsjkvghef">
        <h3 className={`text-sm font-semibold ${subTextColor} uppercase tracking-wider`} data-id="element-2048" data-mp="2gr41otwkpg4a">
          Linked Accounts
        </h3>
        {accounts.map(acc => <div key={acc.id} className={`p-4 rounded-2xl ${cardBg} border ${borderColor} space-y-4`} data-id="element-2049" data-mp="27r78cez8nt2w">
            <div className="flex justify-between items-start" data-id="element-2050" data-mp="34h6pgxtgfzx3">
              <div data-id="element-2051" data-mp="34gohj3xfjx9c">
                <h4 className={`font-semibold ${textColor}`} data-id="element-2052" data-mp="34jatrgv9n0xw">{acc.title}</h4>
                <p className={`text-sm ${subTextColor} mt-1`} data-id="element-2053" data-mp="2zj54g7cgxqiz">{acc.number}</p>
              </div>
              {acc.isDefault && <span className="px-2 py-1 bg-[#F37024]/10 text-[#F37024] text-xs font-medium rounded-full" data-id="element-2054" data-mp="2upacyhqmg79c">
                  Default
                </span>}
            </div>

            <div className={`pt-4 border-t ${borderColor} flex items-center justify-between`} data-id="element-2055" data-mp="3n9is7lo0t37r">
              <span className={`text-sm font-medium ${textColor}`} data-id="element-2056" data-mp="3ifoagnrz6ybb">
                Digital Consent (OTP/MPIN)
              </span>
              <button onClick={() => {
            setAccounts(accounts.map(a => a.id === acc.id ? {
              ...a,
              digitalConsent: !a.digitalConsent
            } : a));
            showNotification('Digital consent updated successfully');
          }} className={`w-12 h-6 rounded-full transition-colors relative ${acc.digitalConsent ? 'bg-[#10B981]' : 'bg-gray-300'}`} data-id="element-2057" data-mp="jtar0sxllshu">
                <motion.div className="w-5 h-5 bg-white rounded-full absolute top-0.5 shadow-sm" animate={{
              left: acc.digitalConsent ? '26px' : '2px'
            }} data-id="element-2058" data-mp="12nz8ll2mkq1c" />
              </button>
            </div>

            {!acc.isDefault && <button onClick={() => {
          setAccounts(accounts.map(a => ({
            ...a,
            isDefault: a.id === acc.id
          })));
          showNotification('Default account updated');
        }} className="w-full py-2 text-sm font-medium text-[#F37024] bg-[#F37024]/10 rounded-xl" data-id="element-2059" data-mp="17gscy7u6lwcw">
                Set as Default
              </button>}
          </div>)}
      </div>

      <div className="space-y-4" data-id="element-2060" data-mp="25352mwxswnnx">
        <h3 className={`text-sm font-semibold ${subTextColor} uppercase tracking-wider`} data-id="element-2061" data-mp="251ac933rg3cg">
          Recent Activity
        </h3>
        <div className={`p-4 rounded-2xl ${cardBg} border ${borderColor}`} data-id="element-2062" data-mp="1q7feot9220gk">
          <div className="flex items-center gap-3 mb-3" data-id="element-2063" data-mp="1v2unv7w4b86n">
            <ShieldIcon className="w-5 h-5 text-[#3B82F6]" data-id="element-2064" data-mp="1hched13kvlf7" />
            <div data-id="element-2065" data-mp="1haxznexklifj">
              <p className={`text-sm font-medium ${textColor}`} data-id="element-2066" data-mp="1m3r3m0l7smqk">
                Digital Consent Enabled
              </p>
              <p className={`text-xs ${subTextColor}`} data-id="element-2067" data-mp="17fmhsnqv7v3k">Today, 10:42 AM</p>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-2068" data-mp="1cd4qqe4vuabk">
            <ShieldIcon className="w-5 h-5 text-[#10B981]" data-id="element-2069" data-mp="1cblc0ryvk7bw" />
            <div data-id="element-2070" data-mp="yfg719mjx983">
              <p className={`text-sm font-medium ${textColor}`} data-id="element-2071" data-mp="yeyeamz3ld4x">
                Default Account Changed
              </p>
              <p className={`text-xs ${subTextColor}`} data-id="element-2072" data-mp="12hl30f0h2hxw">Yesterday, 04:15 PM</p>
            </div>
          </div>
        </div>
      </div>
    </div>;
  const renderLanguage = () => <div className="px-6 pb-24 space-y-6" data-id="element-2073" data-mp="30ae0tkk262ox">
      <div className={`p-2 rounded-2xl ${cardBg} border ${borderColor} flex`} data-id="element-2074" data-mp="2vcchcyiq01cx">
        <button onClick={() => {
        setLanguage('en');
        showNotification('Language set to English');
      }} className={`flex-1 py-3 rounded-xl text-sm font-semibold transition-colors ${language === 'en' ? 'bg-[#661806] text-white' : subTextColor}`} data-id="element-2075" data-mp="2va9g1ic4alsw">
          English
        </button>
        <button onClick={() => {
        setLanguage('ur');
        showNotification('زبان اردو میں تبدیل کر دی گئی');
      }} className={`flex-1 py-3 rounded-xl text-sm font-semibold font-urdu transition-colors ${language === 'ur' ? 'bg-[#661806] text-white' : subTextColor}`} data-id="element-2076" data-mp="3j0dny1wc97pp">
          اردو
        </button>
      </div>
      <p className={`text-sm ${subTextColor} text-center`} data-id="element-2077" data-mp="1ylts1qonokhw">
        Changing the language will update the application interface immediately.
      </p>
    </div>;
  const renderDevices = () => {
    if (deviceToDelete) {
      return <div className="px-6 pb-24 space-y-6" data-id="element-2078" data-mp="i6xmyj7jh0r0">
          <div className="text-center space-y-2" data-id="element-2079" data-mp="i3jq1qhn9jqy">
            <div className="w-16 h-16 bg-[#EF4444]/10 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2080" data-mp="n47bzxr0naeb">
              <Trash2Icon className="w-8 h-8 text-[#EF4444]" data-id="element-2081" data-mp="rxa74abfyrwm" />
            </div>
            <h2 className={`text-xl font-bold ${textColor}`} data-id="element-2082" data-mp="wtqyrehunoeg">Remove Device</h2>
            <p className={`text-sm ${subTextColor}`} data-id="element-2083" data-mp="11tdi85o9u0vp">
              Enter the 6-digit OTP sent to your registered mobile number to
              confirm removal.
            </p>
          </div>

          <div className="flex justify-between gap-2 mt-8" data-id="element-2084" data-mp="1av3cdj9jfdx6">
            {deviceOtp.map((digit, idx) => <input key={idx} id={`otp-${idx}`} type="text" maxLength={1} value={digit} onChange={e => {
            const val = e.target.value.replace(/\D/g, '');
            const newOtp = [...deviceOtp];
            newOtp[idx] = val;
            setDeviceOtp(newOtp);
            if (val && idx < 5) {
              document.getElementById(`otp-${idx + 1}`)?.focus();
            }
          }} onKeyDown={e => {
            if (e.key === 'Backspace' && !digit && idx > 0) {
              document.getElementById(`otp-${idx - 1}`)?.focus();
            }
          }} className={`w-12 h-14 rounded-xl text-center text-xl font-bold ${cardBg} border ${borderColor} ${textColor} focus:border-[#F37024] focus:ring-1 focus:ring-[#F37024] outline-none transition-all`} data-id="element-2085" data-mp="37n3hyo3g6m9r" data-mpi={idx} />)}
          </div>

          <button onClick={() => {
          if (deviceOtp.join('').length === 6) {
            setDevices(devices.filter(d => d.id !== deviceToDelete));
            setDeviceToDelete(null);
            setDeviceOtp(['', '', '', '', '', '']);
            showNotification('Device removed successfully');
          }
        }} disabled={deviceOtp.join('').length !== 6} className="w-full py-4 mt-8 bg-gradient-to-r from-[#D95A1A] to-[#B84A12] text-white font-bold rounded-xl disabled:opacity-50" data-id="element-2086" data-mp="28b39b3l8z2kw">
            Confirm Removal
          </button>
          <button onClick={() => {
          setDeviceToDelete(null);
          setDeviceOtp(['', '', '', '', '', '']);
        }} className={`w-full py-4 font-bold ${subTextColor}`} data-id="element-2087" data-mp="3rat18z0lty11">
            Cancel
          </button>
        </div>;
    }
    return <div className="px-6 pb-24 space-y-6" data-id="element-2088" data-mp="3hhlo2rzmhdqe">
        <div className={`p-4 rounded-2xl bg-[#3B82F6]/10 border border-[#3B82F6]/20 flex gap-3`} data-id="element-2089" data-mp="956cnt5capkh">
          <AlertCircleIcon className="w-5 h-5 text-[#3B82F6] shrink-0 mt-0.5" data-id="element-2090" data-mp="e26ic2ya1qb2" />
          <p className="text-sm text-[#3B82F6]" data-id="element-2091" data-mp="3zrh6rqwqkw">
            You can register up to 3 devices. Removing your current device will
            log you out.
          </p>
        </div>

        <div className="space-y-3" data-id="element-2092" data-mp="2ktp7vo83kkfp">
          {devices.map(device => <div key={device.id} className={`p-4 rounded-2xl ${cardBg} border ${borderColor} flex items-center justify-between`} data-id="element-2093" data-mp="2ks4dbyls91hu">
              <div className="flex items-center gap-4" data-id="element-2094" data-mp="2osy20u71ukbs">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${device.isCurrent ? 'bg-[#10B981]/10 text-[#10B981]' : 'bg-gray-100 text-gray-500'}`} data-id="element-2095" data-mp="vb8tk5h4ipr6">
                  <SmartphoneIcon className="w-5 h-5" data-id="element-2096" data-mp="llyxyfn2moyb" />
                </div>
                <div data-id="element-2097" data-mp="gnxfnwxgytnw">
                  <h4 className={`font-semibold ${textColor} flex items-center gap-2`} data-id="element-2098" data-mp="gofkvj240i7y">
                    {device.name}
                    {device.isCurrent && <span className="px-2 py-0.5 bg-[#10B981] text-white text-[10px] font-bold rounded-full" data-id="element-2099" data-mp="cjzg5l48fgub">
                        CURRENT
                      </span>}
                  </h4>
                  <p className={`text-xs ${subTextColor}`} data-id="element-2100" data-mp="2o4tmeww11m9">
                    {device.os} • Active {device.lastActive}
                  </p>
                </div>
              </div>
              <button onClick={() => setDeviceToDelete(device.id)} className="p-2 text-[#EF4444] hover:bg-[#EF4444]/10 rounded-full transition-colors" data-id="element-2101" data-mp="1x1dujibdbesw">
                <Trash2Icon className="w-5 h-5" data-id="element-2102" data-mp="20k36e6ve27va" />
              </button>
            </div>)}
        </div>
      </div>;
  };
  const renderDeactivate = () => <div className="px-6 pb-24 space-y-6" data-id="element-2103" data-mp="24jvjmwvq926z">
      <div className={`p-4 rounded-2xl bg-[#EF4444]/10 border border-[#EF4444]/20 flex gap-3`} data-id="element-2104" data-mp="29c543xe2pa1v">
        <AlertCircleIcon className="w-5 h-5 text-[#EF4444] shrink-0 mt-0.5" data-id="element-2105" data-mp="2eb8apm9pboby" />
        <p className="text-sm text-[#EF4444]" data-id="element-2106" data-mp="2edccnyo1id9l">
          Deactivating your account will suspend all digital banking access.
          Scheduled transfers may fail.
        </p>
      </div>

      <div className="space-y-4" data-id="element-2107" data-mp="1lmuenc26b231">
        <h3 className={`text-sm font-semibold ${subTextColor} uppercase tracking-wider`} data-id="element-2108" data-mp="1qlnjkg3e9krl">
          Deactivation Type
        </h3>
        <div className="grid grid-cols-2 gap-3" data-id="element-2109" data-mp="1vlry40kqs5f6">
          <button onClick={() => setDeactivateType('temp')} className={`p-4 rounded-2xl border-2 text-center transition-all ${deactivateType === 'temp' ? 'border-[#F37024] bg-[#F37024]/5' : `${borderColor} ${cardBg}`}`} data-id="element-2110" data-mp="1vidvqsc2vw7q">
            <ClockIcon className={`w-6 h-6 mx-auto mb-2 ${deactivateType === 'temp' ? 'text-[#F37024]' : subTextColor}`} data-id="element-2111" data-mp="3s3ofolvxjzzl" />
            <span className={`text-sm font-semibold ${deactivateType === 'temp' ? 'text-[#F37024]' : textColor}`} data-id="element-2112" data-mp="3o35omu1v2xyg">
              Temporary
            </span>
          </button>
          <button onClick={() => setDeactivateType('perm')} className={`p-4 rounded-2xl border-2 text-center transition-all ${deactivateType === 'perm' ? 'border-[#EF4444] bg-[#EF4444]/5' : `${borderColor} ${cardBg}`}`} data-id="element-2113" data-mp="xws9u9jf1gh">
            <LockIcon className={`w-6 h-6 mx-auto mb-2 ${deactivateType === 'perm' ? 'text-[#EF4444]' : subTextColor}`} data-id="element-2114" data-mp="ao9dlop6eyno" />
            <span className={`text-sm font-semibold ${deactivateType === 'perm' ? 'text-[#EF4444]' : textColor}`} data-id="element-2115" data-mp="omph76rqs91l">
              Permanent
            </span>
          </button>
        </div>
      </div>

      {deactivateType === 'temp' && <div className="space-y-2" data-id="element-2116" data-mp="2qgf9wpm5flim">
          <label className={`text-sm font-medium ${textColor}`} data-id="element-2117" data-mp="2lgatt0p5kssx">
            Duration (Days): {deactivateDays}
          </label>
          <input type="range" min="1" max="30" value={deactivateDays} onChange={e => setDeactivateDays(parseInt(e.target.value))} className="w-full accent-[#F37024]" data-id="element-2118" data-mp="10y0bfabhf6sa" />
          <div className="flex justify-between text-xs text-gray-500" data-id="element-2119" data-mp="s5ycjgh29yk4">
            <span data-id="element-2120" data-mp="w6qle8m0u1fv">1 Day</span>
            <span data-id="element-2121" data-mp="w5yp0vklnzoj">30 Days</span>
          </div>
        </div>}

      {deactivateType === 'perm' && <div className={`p-4 rounded-2xl ${cardBg} border ${borderColor} flex gap-3`} data-id="element-2122" data-mp="i6fu54gsuku6">
          <ShieldIcon className="w-5 h-5 text-[#EF4444] shrink-0 mt-0.5" data-id="element-2123" data-mp="n5sijtnbjzyv" />
          <p className={`text-sm ${subTextColor}`} data-id="element-2124" data-mp="n3pgucct0ieb">
            Permanent deactivation requires branch / Contact Centre KYC to
            reactivate. Your devices will be unbound and scheduled transactions
            cancelled.
          </p>
        </div>}

      <button onClick={() => {
      requestMpin(() => {
        showNotification(`Account deactivated ${deactivateType === 'temp' ? `for ${deactivateDays} day${deactivateDays > 1 ? 's' : ''}` : 'permanently'}`, 'error');
        setTimeout(() => onBack(), 1800);
      });
    }} className="w-full py-4 mt-8 bg-[#EF4444] text-white font-bold rounded-xl active:scale-[0.98] transition-transform" data-id="element-2125" data-mp="23ld60aa2a8ah">
        Deactivate Account
      </button>
    </div>;
  const renderChangePassword = () => {
    const rules = [{
      label: 'At least 8 characters',
      ok: newPassword.length >= 8
    }, {
      label: 'One uppercase letter',
      ok: /[A-Z]/.test(newPassword)
    }, {
      label: 'One number',
      ok: /[0-9]/.test(newPassword)
    }, {
      label: 'One special character',
      ok: /[^A-Za-z0-9]/.test(newPassword)
    }];
    const allRulesPass = rules.every(r => r.ok);
    const matches = newPassword.length > 0 && newPassword === confirmPassword;
    const canSubmit = currentPassword.length > 0 && allRulesPass && matches;
    const PasswordField = ({
      label,
      value,
      onChange,
      show,
      toggle,
      placeholder
    }: {
      label: string;
      value: string;
      onChange: (v: string) => void;
      show: boolean;
      toggle: () => void;
      placeholder: string;
    }) => <div className="space-y-2" data-id="element-2126" data-mp="23dgvupxvgvjc">
        <label className={`text-sm font-medium ${textColor}`} data-id="element-2127" data-mp="2hdhtzxmr5ov4">{label}</label>
        <div className="relative" data-id="element-2128" data-mp="2hbdt7ok5h5z2">
          <div className="absolute left-4 top-1/2 -translate-y-1/2" data-id="element-2129" data-mp="2dbd67fizjmge">
            <LockIcon className={`w-5 h-5 ${subTextColor}`} data-id="element-2130" data-mp="2da22ffpjiepa" />
          </div>
          <input type={show ? 'text' : 'password'} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} className={`w-full p-4 pl-12 pr-12 rounded-xl ${cardBg} border ${borderColor} ${textColor} focus:outline-none focus:border-[#F37024]`} data-id="element-2131" data-mp="1pmk5z6vbysfz" />
          <button type="button" onClick={toggle} className="absolute right-4 top-1/2 -translate-y-1/2" data-id="element-2132" data-mp="1tmun9fokp5a5">
            {show ? <EyeOffIcon className={`w-5 h-5 ${subTextColor}`} data-id="element-2133" data-mp="15x0a0x0cdepd" /> : <EyeIcon className={`w-5 h-5 ${subTextColor}`} data-id="element-2134" data-mp="11wzlgjjj3n4l" />}
          </button>
        </div>
      </div>;
    return <div className="px-6 pb-24 space-y-6" data-id="element-2135" data-mp="312128l61jf7l">
        <div className={`p-4 rounded-2xl bg-[#0EA5E9]/10 border border-[#0EA5E9]/20 flex gap-3`} data-id="element-2136" data-mp="310y906l8x0oi">
          <ShieldIcon className="w-5 h-5 text-[#0EA5E9] shrink-0 mt-0.5" data-id="element-2137" data-mp="35yzrapaukvyx" />
          <p className="text-sm text-[#0EA5E9]" data-id="element-2138" data-mp="3asc7cb1gnug2">
            Choose a strong password you haven't used before. You'll confirm
            with your MPIN.
          </p>
        </div>

        <PasswordField label="Current Password" value={currentPassword} onChange={setCurrentPassword} show={showCurrent} toggle={() => setShowCurrent(!showCurrent)} placeholder="Enter current password" data-id="element-2139" data-mp="2i356h5ejasrn" />
        <PasswordField label="New Password" value={newPassword} onChange={v => {
        setNewPassword(v);
        setPasswordError(null);
      }} show={showNew} toggle={() => setShowNew(!showNew)} placeholder="Enter new password" data-id="element-2140" data-mp="2s0jo38c71nlj" />

        {newPassword.length > 0 && <div className={`p-4 rounded-2xl ${cardBg} border ${borderColor} space-y-2`} data-id="element-2141" data-mp="7cwwj7l9dgtv">
            {rules.map(r => <div key={r.label} className="flex items-center gap-2" data-id="element-2142" data-mp="1wrpu6rrvyjl3">
                <CheckCircleIcon className={`w-4 h-4 ${r.ok ? 'text-[#10B981]' : subTextColor + ' opacity-40'}`} data-id="element-2143" data-mp="1wqwirdhzf5wm" />
                <span className={`text-xs ${r.ok ? 'text-[#10B981]' : subTextColor}`} data-id="element-2144" data-mp="1ruz3laigg5lf">
                  {r.label}
                </span>
              </div>)}
          </div>}

        <PasswordField label="Confirm New Password" value={confirmPassword} onChange={setConfirmPassword} show={showConfirm} toggle={() => setShowConfirm(!showConfirm)} placeholder="Re-enter new password" data-id="element-2145" data-mp="1i2a65jjqos93" />
        {confirmPassword.length > 0 && !matches && <p className="text-xs text-[#EF4444]" data-id="element-2146" data-mp="142sb4mfuoira">Passwords do not match.</p>}

        <button onClick={() => {
        if (currentPassword === newPassword) {
          setPasswordError('New password must differ from current password.');
          return;
        }
        requestMpin(() => {
          setCurrentPassword('');
          setNewPassword('');
          setConfirmPassword('');
          showNotification('Password changed successfully');
          goBack();
        });
      }} disabled={!canSubmit} className="w-full py-4 mt-4 bg-gradient-to-r from-[#D95A1A] to-[#B84A12] text-white font-bold rounded-xl disabled:opacity-50" data-id="element-2147" data-mp="144vcu3qd80bu">
          Update Password
        </button>
        {passwordError && <p className="text-xs text-[#EF4444] text-center" data-id="element-2148" data-mp="1m57jwb8dczho">{passwordError}</p>}
      </div>;
  };
  const renderLoginMethod = () => <div className="px-6 pb-24 space-y-6" data-id="element-2149" data-mp="17fifm74zcfd6">
      <div className="space-y-3" data-id="element-2150" data-mp="17egt96iik6g5">
        {[{
        id: 'biometric',
        icon: ScanFaceIcon,
        title: 'Biometric (Face ID / Touch ID)',
        desc: 'Fastest and most secure',
        supported: true
      }, {
        id: 'mpin',
        icon: KeyIcon,
        title: '4-Digit MPIN',
        desc: 'Quick access via PIN',
        supported: true
      }, {
        id: 'password',
        icon: LockIcon,
        title: 'Password',
        desc: 'Traditional login',
        supported: false
      }].map(method => <button key={method.id} disabled={!method.supported} onClick={() => setLoginMethod(method.id as any)} className={`w-full p-4 rounded-2xl border-2 flex items-center gap-4 transition-all text-left
              ${!method.supported ? 'opacity-50 cursor-not-allowed' : ''}
              ${loginMethod === method.id ? 'border-[#F37024] bg-[#F37024]/5' : `${borderColor} ${cardBg}`}
            `} data-id="element-2151" data-mp="9v0b8xgjni6r">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${loginMethod === method.id ? 'bg-[#F37024] text-white' : 'bg-gray-100 text-gray-500'}`} data-id="element-2152" data-mp="stmfumb9rrff">
              <method.icon className="w-5 h-5" data-id="element-2153" data-mp="oqgd3ond8idp" />
            </div>
            <div className="flex-1" data-id="element-2154" data-mp="12icryr00wvs5">
              <h4 className={`font-semibold ${textColor}`} data-id="element-2155" data-mp="12liebyh9pjk6">{method.title}</h4>
              <p className={`text-xs ${subTextColor}`} data-id="element-2156" data-mp="xlctb15i6vb2">
                {method.supported ? method.desc : 'Not supported on this device'}
              </p>
            </div>
            {loginMethod === method.id && <CheckCircleIcon className="w-6 h-6 text-[#F37024]" data-id="element-2157" data-mp="17kli7flihjqk" />}
          </button>)}
      </div>
      <button onClick={() => {
      showNotification('Default login method updated');
      goBack();
    }} className="w-full py-4 mt-8 bg-gradient-to-r from-[#D95A1A] to-[#B84A12] text-white font-bold rounded-xl" data-id="element-2158" data-mp="1ens3qd7ywg35">
        Save Preference
      </button>
    </div>;
  const renderFaceId = () => <div className="px-6 pb-24 space-y-8 text-center" data-id="element-2159" data-mp="1tcfzajhtcpt3">
      <div className="w-24 h-24 mx-auto bg-gradient-to-br from-[#6366F1] to-[#8B5CF6] rounded-full flex items-center justify-center shadow-lg" data-id="element-2160" data-mp="1yahgeyvoif1x">
        <ScanFaceIcon className="w-12 h-12 text-white" data-id="element-2161" data-mp="229q7tx38y0ub" />
      </div>

      <div data-id="element-2162" data-mp="27bnyvdhw1zxt">
        <h2 className={`text-2xl font-bold ${textColor} mb-2`} data-id="element-2163" data-mp="27bxo2yv4032g">
          Facial Recognition
        </h2>
        <p className={`text-sm ${subTextColor}`} data-id="element-2164" data-mp="2c97ccb6ri8op">
          Use Face ID to log in quickly and securely without entering your MPIN.
        </p>
      </div>

      <div className={`p-4 rounded-2xl ${cardBg} border ${borderColor} flex items-center justify-between`} data-id="element-2165" data-mp="hvg8jsb46j16">
        <span className={`font-semibold ${textColor}`} data-id="element-2166" data-mp="acoxr2cqtan7">Enable Face ID</span>
        <button onClick={() => setFaceIdEnabled(!faceIdEnabled)} className={`w-14 h-7 rounded-full transition-colors relative ${faceIdEnabled ? 'bg-[#10B981]' : 'bg-gray-300'}`} data-id="element-2167" data-mp="5lgt0jmcydgd">
          <motion.div className="w-6 h-6 bg-white rounded-full absolute top-0.5 shadow-sm" animate={{
          left: faceIdEnabled ? '30px' : '2px'
        }} data-id="element-2168" data-mp="k9l8kk7te9p" />
        </button>
      </div>

      <button onClick={() => {
      showNotification('Biometric settings saved');
      goBack();
    }} className="w-full py-4 mt-8 bg-gradient-to-r from-[#D95A1A] to-[#B84A12] text-white font-bold rounded-xl" data-id="element-2169" data-mp="ta9oyg2zclaz">
        Save Changes
      </button>
    </div>;
  const renderUsername = () => <div className="px-6 pb-24 space-y-6" data-id="element-2170" data-mp="ed8vo8554iae">
      <div className="space-y-2" data-id="element-2171" data-mp="3432pu7v0msrq">
        <label className={`text-sm font-medium ${subTextColor}`} data-id="element-2172" data-mp="3445h4gw92x86">
          Current Username
        </label>
        <div className={`w-full p-4 rounded-xl ${cardBg} border ${borderColor} ${subTextColor} bg-opacity-50`} data-id="element-2173" data-mp="2z383mhm6qrd0">
          kashish
        </div>
      </div>

      <div className="space-y-2" data-id="element-2174" data-mp="3tqyi3pjhug3i">
        <label className={`text-sm font-medium ${textColor}`} data-id="element-2175" data-mp="3ts9ntuwi1xx9">
          New Username
        </label>
        <div className="relative" data-id="element-2176" data-mp="3fss2xqmbr4t6">
          <input type="text" value={newUsername} onChange={e => handleUsernameCheck(e.target.value)} placeholder="Enter new username" className={`w-full p-4 rounded-xl ${cardBg} border ${usernameAvailable === false ? 'border-[#EF4444]' : usernameAvailable === true ? 'border-[#10B981]' : borderColor} ${textColor} focus:outline-none focus:ring-2 focus:ring-[#F37024]/50`} data-id="element-2177" data-mp="3ftjkhxj3720g" />
          {usernameAvailable === true && <CheckCircleIcon className="absolute right-4 top-4 w-6 h-6 text-[#10B981]" data-id="element-2178" data-mp="2j44acs1dmf5" />}
          {usernameAvailable === false && <AlertCircleIcon className="absolute right-4 top-4 w-6 h-6 text-[#EF4444]" data-id="element-2179" data-mp="uccwdisgznbs" />}
        </div>
        {usernameAvailable === false && <p className="text-xs text-[#EF4444]" data-id="element-2180" data-mp="z3uw63irbhv6">
            Username is already taken. Try: kashish_99, kshewani1
          </p>}
        {usernameAvailable === true && <p className="text-xs text-[#10B981]" data-id="element-2181" data-mp="2eu5f02k9sczr">Username is available!</p>}
        <p className={`text-xs ${subTextColor}`} data-id="element-2182" data-mp="2es2ozij4ajv1">
          Must be at least 4 characters. Can contain letters, numbers, and
          underscores.
        </p>
      </div>

      <button onClick={() => {
      showNotification('Username updated successfully');
      goBack();
    }} disabled={!usernameAvailable} className="w-full py-4 mt-8 bg-gradient-to-r from-[#D95A1A] to-[#B84A12] text-white font-bold rounded-xl disabled:opacity-50" data-id="element-2183" data-mp="2ol9paoz039o9">
        Update Username
      </button>
    </div>;
  const renderContact = () => <div className="px-6 pb-24 space-y-6" data-id="element-2184" data-mp="25tgwovnufqh2">
      <div className="space-y-4" data-id="element-2185" data-mp="25sfjoln9otwp">
        <div className="space-y-2" data-id="element-2186" data-mp="25vjx89mmh201">
          <label className={`text-sm font-medium ${textColor}`} data-id="element-2187" data-mp="3m7gxe8uijk5d">
            Mobile Number
          </label>
          <div className="relative" data-id="element-2188" data-mp="3h95njy47x9n5">
            <div className="absolute left-4 top-4" data-id="element-2189" data-mp="3h84bbqbguh3u">
              <PhoneIcon className={`w-5 h-5 ${subTextColor}`} data-id="element-2190" data-mp="3vvqc3oyo8ltl" />
            </div>
            <input type="text" value={contactMobile} onChange={e => setContactMobile(e.target.value)} className={`w-full p-4 pl-12 rounded-xl ${cardBg} border ${borderColor} ${textColor} focus:outline-none focus:border-[#F37024]`} data-id="element-2191" data-mp="3qw401iukxbmd" />
          </div>
        </div>

        <div className="space-y-2" data-id="element-2192" data-mp="3hx0hcfzbqy8e">
          <label className={`text-sm font-medium ${textColor}`} data-id="element-2193" data-mp="igarbq6hp4fv">
            Email Address
          </label>
          <div className="relative" data-id="element-2194" data-mp="dhhgy6mi1tjx">
            <div className="absolute left-4 top-4" data-id="element-2195" data-mp="diioi14b3xww">
              <MailIcon className={`w-5 h-5 ${subTextColor}`} data-id="element-2196" data-mp="8qheymyiwlak" />
            </div>
            <input type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} className={`w-full p-4 pl-12 rounded-xl ${cardBg} border ${borderColor} ${textColor} focus:outline-none focus:border-[#F37024]`} data-id="element-2197" data-mp="8o4r5t633gpl" />
          </div>
        </div>

        <div className="space-y-2" data-id="element-2198" data-mp="wfa68647a4yu">
          <label className={`text-sm font-medium ${textColor}`} data-id="element-2199" data-mp="we8tlx7jdaf0">
            Postal Address
          </label>
          <div className="relative" data-id="element-2200" data-mp="2k1wqgsgmubdt">
            <div className="absolute left-4 top-4" data-id="element-2201" data-mp="2k2y14vtqkvv0">
              <MapPinIcon className={`w-5 h-5 ${subTextColor}`} data-id="element-2202" data-mp="2k3rb05nzs1hd" />
            </div>
            <textarea value={contactAddress} onChange={e => setContactAddress(e.target.value)} rows={3} className={`w-full p-4 pl-12 rounded-xl ${cardBg} border ${borderColor} ${textColor} focus:outline-none focus:border-[#F37024] resize-none`} data-id="element-2203" data-mp="2o26x87c915tq" />
          </div>
        </div>
      </div>

      <button onClick={() => {
      showNotification('Contact details updated. OTP sent for verification.');
      goBack();
    }} className="w-full py-4 mt-8 bg-gradient-to-r from-[#D95A1A] to-[#B84A12] text-white font-bold rounded-xl" data-id="element-2204" data-mp="32z7ok9qj2yro">
        Save Details
      </button>
    </div>;
  const getTitle = () => {
    switch (step) {
      case 'home':
        return 'My Profile';
      case 'account_settings':
        return 'Account Settings';
      case 'language':
        return 'Language';
      case 'devices':
        return 'Device Management';
      case 'deactivate':
        return 'Deactivate Account';
      case 'login_method':
        return 'Login Method';
      case 'face_id':
        return 'Facial Recognition';
      case 'username':
        return 'Update Username';
      case 'change_password':
        return 'Change Password';
      case 'contact':
        return 'Contact Details';
    }
  };
  return <div className={`fixed inset-0 z-50 ${bgClass} overflow-hidden flex flex-col`} data-id="element-2205" data-mp="24hd0ary9vrwt">
      {renderHeader(getTitle())}

      <div className="flex-1 relative overflow-y-auto hide-scrollbar" data-id="element-2206" data-mp="2edfxrki6j2df">
        <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-2207" data-mp="2ebllsvovyklh">
          <motion.div key={step} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
          type: 'spring',
          stiffness: 300,
          damping: 30
        }} className="absolute inset-0 w-full h-full" data-id="element-2208" data-mp="29f4q9gfcx3yd">
            {step === 'home' && renderHome()}
            {step === 'account_settings' && renderAccountSettings()}
            {step === 'language' && renderLanguage()}
            {step === 'devices' && renderDevices()}
            {step === 'deactivate' && renderDeactivate()}
            {step === 'login_method' && renderLoginMethod()}
            {step === 'face_id' && renderFaceId()}
            {step === 'username' && renderUsername()}
            {step === 'change_password' && renderChangePassword()}
            {step === 'contact' && renderContact()}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Inline MPIN confirmation sheet */}
      <AnimatePresence data-id="element-2209" data-mp="1y1mgd08bmnjd">
        {showMpinSheet && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => {
          setShowMpinSheet(false);
          setMpin('');
          mpinActionRef.current = null;
        }} className="absolute inset-0 bg-black/50 z-[60]" data-id="element-2210" data-mp="22z4jub3kkajz" />
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 28,
          stiffness: 300
        }} className={`absolute inset-x-0 bottom-0 z-[61] ${cardBg} rounded-t-3xl p-6 pb-8`} data-id="element-2211" data-mp="1x80i652o18mp">
              <div className="flex items-center justify-between mb-2" data-id="element-2212" data-mp="1eb0e7hkrejww">
                <h3 className={`text-lg font-bold ${textColor}`} data-id="element-2213" data-mp="1edd4qj4l22lk">
                  Confirm with MPIN
                </h3>
                <button onClick={() => {
              setShowMpinSheet(false);
              setMpin('');
              mpinActionRef.current = null;
            }} className={`w-8 h-8 rounded-full flex items-center justify-center ${isLight ? 'bg-[#F5F3F1]' : 'bg-white/10'}`} data-id="element-2214" data-mp="15iyifwlmxrsy">
                  <XIcon className={`w-4 h-4 ${textColor}`} data-id="element-2215" data-mp="vmvj0yi6479p" />
                </button>
              </div>
              <p className={`text-sm ${subTextColor} mb-6`} data-id="element-2216" data-mp="qo0oizau8u9r">
                Enter your 4-digit MPIN to authorize this action.
              </p>

              {/* MPIN dots */}
              <div className="flex justify-center gap-3 mb-8" data-id="element-2217" data-mp="lezkytfpx109">
                {[0, 1, 2, 3].map(i => <motion.div key={i} animate={mpinError ? {
              x: [-5, 5, -5, 5, 0]
            } : {}} className={`w-12 h-14 rounded-xl flex items-center justify-center text-2xl font-bold border-2 ${mpin[i] ? 'border-[#F37024] ' + (isLight ? 'bg-white' : 'bg-white/5') : borderColor + ' ' + (isLight ? 'bg-[#F5F3F1]' : '')} ${textColor}`} data-id="element-2218" data-mp="qggjwjen6de7">
                    {mpin[i] ? '•' : ''}
                  </motion.div>)}
              </div>

              {/* Keypad */}
              <div className="grid grid-cols-3 gap-3 max-w-[280px] mx-auto" data-id="element-2219" data-mp="cge2sze5ro00">
                {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map(n => <button key={n} onClick={() => handleMpinDigit(n)} className={`h-14 rounded-2xl text-2xl font-medium active:scale-95 transition-transform ${textColor} ${isLight ? 'bg-[#F5F3F1]' : 'bg-white/5'}`} data-id="element-2220" data-mp="3b5w58yv3d9wg">
                    {n}
                  </button>)}
                <div data-id="element-2221" data-mp="336887s52cuyn" />
                <button onClick={() => handleMpinDigit('0')} className={`h-14 rounded-2xl text-2xl font-medium active:scale-95 transition-transform ${textColor} ${isLight ? 'bg-[#F5F3F1]' : 'bg-white/5'}`} data-id="element-2222" data-mp="334x7603061b8">
                  0
                </button>
                <button onClick={() => setMpin(mpin.slice(0, -1))} className={`h-14 rounded-2xl flex items-center justify-center active:scale-95 transition-transform ${textColor} ${isLight ? 'bg-[#F5F3F1]' : 'bg-white/5'}`} data-id="element-2223" data-mp="2p5fcx56xtvuh">
                  <DeleteIcon className="w-6 h-6" data-id="element-2224" data-mp="2z4whfjage3ov" />
                </button>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>

      {/* Toast Notification */}
      <AnimatePresence data-id="element-2225" data-mp="ecupb6exsvpg">
        {showToast && <motion.div initial={{
        opacity: 0,
        y: 50
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 50
      }} className="absolute bottom-8 left-6 right-6 z-50" data-id="element-2226" data-mp="ebt1008wucps">
            <div className={`p-4 rounded-xl shadow-lg flex items-center gap-3 ${showToast.type === 'success' ? 'bg-[#10B981] text-white' : 'bg-[#EF4444] text-white'}`} data-id="element-2227" data-mp="lx59r44glgw8">
              {showToast.type === 'success' ? <CheckCircleIcon className="w-5 h-5" data-id="element-2228" data-mp="byzciztlkyxp" /> : <AlertCircleIcon className="w-5 h-5" data-id="element-2229" data-mp="c00p58q9hthj" />}
              <p className="text-sm font-medium" data-id="element-2230" data-mp="zojtxea3ro4v">{showToast.message}</p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}